import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
}));

const GoogleContactsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState({ connected: false, email: null, lastSyncAt: null });

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/google-contacts/status");
      setStatus(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchStatus();
  }, [open]);

  const handleConnect = async () => {
    try {
      const { data } = await api.get("/google-contacts/auth-url");
      window.location.href = data.url;
    } catch (err) {
      toastError(err);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/google-contacts/sync");
      let msg =
        `${data.pulledFromGoogle} contato(s) trazido(s) do Google, ` +
        `${data.pushedToGoogle} enviado(s) do SacZap pro Google.`;
      if (data.pushRemaining > 0) {
        msg += ` Ainda restam ${data.pushRemaining} pra enviar — clique em sincronizar de novo.`;
      }
      toast.success(msg);
      fetchStatus();
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.delete("/google-contacts/disconnect");
      toast.success("Google Contatos desconectado.");
      fetchStatus();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Sincronizar Google Contatos</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress />
          </Box>
        ) : status.connected ? (
          <>
            <Typography variant="body1" gutterBottom>
              Conectado como <strong>{status.email}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {status.lastSyncAt
                ? `Última sincronização: ${new Date(status.lastSyncAt).toLocaleString("pt-BR")}`
                : "Ainda não sincronizado."}
            </Typography>
            <Typography variant="body2" style={{ marginTop: 16 }}>
              A sincronização é nos dois sentidos: contatos que só existem no Google são
              trazidos pro SacZap, e contatos que só existem no SacZap são enviados pro
              Google. Quem já existe nos dois (mesmo número) não é alterado nem duplicado.
              O envio pro Google é feito em lotes — se tiver muita coisa nova, pode precisar
              clicar em sincronizar mais de uma vez.
            </Typography>
          </>
        ) : (
          <Typography variant="body1">
            Conecte a conta Google do escritório (contec.ctb@gmail.com) para sincronizar os
            contatos entre o Google e o SacZap.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Fechar
        </Button>
        {!loading && status.connected && (
          <>
            <Button onClick={handleDisconnect} color="secondary">
              Desconectar
            </Button>
            <Button
              onClick={handleSync}
              color="primary"
              variant="contained"
              disabled={syncing}
            >
              {syncing ? <CircularProgress size={20} /> : "Sincronizar agora"}
            </Button>
          </>
        )}
        {!loading && !status.connected && (
          <Button onClick={handleConnect} color="primary" variant="contained">
            Conectar com Google
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GoogleContactsModal;
